import { useEffect, useRef, useState } from 'react';
import { Group as LayerGroup, Vector as VectorLayer } from 'ol/layer';
import { ItemDataType } from 'rsuite/lib/@types/common';
import { Vector as VectorSource } from 'ol/source';
import GeoJSON from 'ol/format/GeoJSON';

import { useI18n } from 'i18n';
import { IGeoJsonLayer } from 'types/map';
import { ATOMS_LAYERS_GROUPS, ATOMS_LAYERS_GROUPS_IDS, ATOMS_LAYERS_OPTIONS, GEO_JSONS_INFO } from 'constants/map';
import { FETCH_OPTIONS } from 'constants/api';
import { getVectorStyles } from 'helpers/map/getVectorStyles';
import useLocalStorage from 'core/misc/useLocalStorage';
import { Map, getOwMap } from 'core/react/general/location/Map';

export const useGeoJsonLayers = (mapName: string, map: Map | null) => {
  const { f } = useI18n();

  const geoJsonLayerGroup = useRef<LayerGroup | null>(null);

  const [selectedGeoJsonLayers, setSelectedGeoJsonLayers] = useLocalStorage(`${mapName}_geo_json_layers`);
  const [geoJsonLayersOptions, setGeoJsonLayersOptions] = useState<Record<string, string>[] | null>(null);
  const [selectedAtomsLayers, setSelectedAtomsLayers] = useLocalStorage(`${mapName}_atoms_layers`);
  const [atomsLayersOptions, setAtomsLayersOptions] = useState<Record<string, string>[] | null>(null);

  const getCurrentZoom = () => Math.trunc(map?.getOlMap()?.getView()?.getZoom() || 1);

  const getGeoJsonLayerGroup = (geo: IGeoJsonLayer[]) => {
    const layers = geo.map(({ data, name, zoom, color, labelKey }) => {
      const vectorSource = new VectorSource({
        features: new GeoJSON({ featureProjection: 'EPSG:3857' }).readFeatures(data),
      });

      const layer = new VectorLayer({ source: vectorSource });

      layer.setStyle(getVectorStyles(color, labelKey));
      layer.setProperties({ name, zoom });

      return layer;
    });

    return new LayerGroup({ layers });
  };

  const fetchGeoJson = async () =>
    Promise.all(
      GEO_JSONS_INFO.map(async ({ path, ...rest }) => {
        const data = await (await fetch(path, FETCH_OPTIONS)).json();

        return { data, ...rest };
      }),
    );

  const setGeoJsonVisibility = (layersNames: string[], zoom?: number) => {
    const currentZoom = zoom || getCurrentZoom();

    if (geoJsonLayerGroup?.current) {
      geoJsonLayerGroup.current.getLayers().forEach((layer) => {
        layer.setVisible(layersNames?.includes(layer.get('name')) && currentZoom >= layer.get('zoom'));
      });
    }
  };

  const setAtomsLayersVisibility = (groupsIds: string[]) => {
    const owMap = getOwMap(mapName);

    const layerIds: string[] = [];

    if (groupsIds?.length) {
      groupsIds.forEach((id: string) => ATOMS_LAYERS_GROUPS[id] && layerIds.push(...ATOMS_LAYERS_GROUPS[id]));
    }

    if (owMap && owMap.atomsLayer) {
      owMap.atomsLayer.setLayersVisible(layerIds);
    }
  };

  const setLayersOptions = (geo: IGeoJsonLayer[]) => {
    const layersOptions: Record<string, string>[] = geo.reduce(
      (options: Record<string, string>[], layer: IGeoJsonLayer) => {
        const isOptionExist = !!options.find((option) => option.value === layer.name);

        return isOptionExist ? options : [...options, { value: layer.name, label: f(layer.name) }];
      },
      [],
    );

    setGeoJsonLayersOptions(layersOptions);
  };

  const createAtomsLayersOptions = (layers: any[]) => {
    if (!layers || !layers.length) return;

    const layersOptions: Record<string, string>[] = layers.reduce((options: Record<string, string>[], layer: any) => {
      if (!layer.id) return options;

      const isOptionExist = !!options.find((option) => option.value === layer.id);

      return isOptionExist ? options : [...options, { value: layer.id, label: f(layer.id) }];
    }, []);

    setAtomsLayersOptions(layersOptions);
  };

  const setLayers = async () => {
    if (map) {
      const geoJsonLayers = await fetchGeoJson();

      setLayersOptions(geoJsonLayers);
      geoJsonLayerGroup.current = getGeoJsonLayerGroup(geoJsonLayers);
      map.getOlMap().addLayer(geoJsonLayerGroup.current);
      setGeoJsonVisibility(selectedGeoJsonLayers);

      setAtomsLayersOptions(ATOMS_LAYERS_OPTIONS);
      setAtomsLayersVisibility(selectedAtomsLayers || []);
    }
  };

  const onGeoJsonLayerSelect = (zones: string[]) => {
    setSelectedGeoJsonLayers(zones);
    setGeoJsonVisibility(zones);
  };

  const getSelectedAtomsLayersIds = (ids: string[], toggledId?: string) => {
    const isAllToggled = toggledId === 'all';
    const isAllSelected = ids.includes('all');

    if (isAllToggled && isAllSelected) {
      return ['all', ...ATOMS_LAYERS_GROUPS_IDS];
    }

    if (!ids.length || (isAllToggled && !isAllSelected)) {
      return [];
    }

    if (!isAllToggled && isAllSelected) {
      return [...ids].filter((id) => id !== 'all');
    }

    return ids.length === ATOMS_LAYERS_GROUPS_IDS.length ? ['all', ...ids] : ids;
  };

  const onAtomsLayerSelect = (layerIds: string[], selectedId?: ItemDataType) => {
    const selectedIds = getSelectedAtomsLayersIds(layerIds, selectedId?.value);

    setSelectedAtomsLayers(selectedIds);
    setAtomsLayersVisibility(selectedIds);
  };

  useEffect(() => {
    void setLayers();
  }, [map]);

  useEffect(() => {
    if (map) {
      let currentZoom = getCurrentZoom();

      map
        .getOlMap()
        .getView()
        .on('change:resolution', () => {
          const newZoom = getCurrentZoom();

          if (currentZoom !== newZoom) {
            currentZoom = newZoom;

            geoJsonLayerGroup.current && setGeoJsonVisibility(selectedGeoJsonLayers, newZoom);
          }
        });
    }
  }, [map, selectedGeoJsonLayers]);

  useEffect(() => {
    if (selectedAtomsLayers?.length) {
      setAtomsLayersVisibility(selectedAtomsLayers);
    }
  }, [selectedAtomsLayers]);

  return {
    geoJsonLayersOptions,
    selectedGeoJsonLayers,
    onGeoJsonLayerSelect,
    atomsLayersOptions,
    selectedAtomsLayers,
    onAtomsLayerSelect,
  };
};
